import { Row } from 'antd';
import styled from 'styled-components';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';

export const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;

  .numeric-cell {
    font-variant-numeric: tabular-nums;
  }
`;

export const SummaryRow = styled(Row)`
  margin: 0;
`;

/**
 * One summary figure.
 *
 * `$money` marks the cards carrying a value rather than a count. A row of four
 * identical cards reads as one undifferentiated block, and the first question
 * anyone asks of it is how much, not how many — so the money cards are given a
 * tinted edge to separate the two kinds at a glance without adding a heading
 * above each pair.
 */
export const SummaryCard = styled.div<{ $money?: boolean }>`
  flex: 1 1 160px;
  min-width: 140px;
  padding: 9px 14px;
  background: ${APP_SHELL_THEME.metricCardBg};
  border: 1px solid ${APP_SHELL_THEME.metricCardBorder};
  border-radius: 8px;
  /* Left edge rather than a full border: it marks the card without competing
     with the figure, which is what should carry the weight. */
  border-left: ${({ $money }) =>
    $money ? `2px solid ${APP_SHELL_THEME.accentText}` : `1px solid ${APP_SHELL_THEME.metricCardBorder}`};
`;

export const SummaryLabel = styled.div`
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${APP_SHELL_THEME.textMuted};
  margin-bottom: 2px;
`;

export const SummaryValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${APP_SHELL_THEME.text};
  /* Figures line up under each other as the grid reflows, and a value that
     changes on refresh does not shift the ones beside it. */
  font-variant-numeric: tabular-nums;
`;

/** Placeholder while a figure loads, so an empty card is not read as zero. */
export const SummarySkeleton = styled.div`
  height: 22px;
  width: 64px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(${APP_SHELL_THEME.overlayRgb}, 0.06) 25%,
    rgba(${APP_SHELL_THEME.overlayRgb}, 0.12) 37%,
    rgba(${APP_SHELL_THEME.overlayRgb}, 0.06) 63%
  );
  background-size: 400% 100%;
  animation: summaryShimmer 1.4s ease infinite;

  @keyframes summaryShimmer {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }

  /* A figure that is merely loading does not warrant motion for someone who
     has asked for less of it. */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px;
`;

/**
 * This page's header bar.
 *
 * It carries the filters instead of a page title: the sidebar already names
 * the page, so the heading was pure height above data the user came to read.
 * Matches the shared page header's surface and rule so the app still looks
 * consistent from page to page.
 */
export const FilterBarHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 10px 24px;
  background: ${APP_SHELL_THEME.pageBg};
  border-bottom: 1px solid ${APP_SHELL_THEME.panelBorder};
`;

/**
 * The page name, set beside the filters rather than above them.
 *
 * Every other page carries a heading, and dropping this one entirely made the
 * page read as missing an element rather than as a deliberate layout. Inline
 * it costs no vertical space while keeping the app consistent.
 */
export const FilterBarTitle = styled.h1`
  margin: 0;
  flex: 0 0 auto;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${APP_SHELL_THEME.text};
  white-space: nowrap;
  align-self: flex-start;
  padding-top: 4px;
`;

/**
 * The page's filter bar, sitting in the header beside the title.
 *
 * It governs both tabs, so it belongs with the page identity rather than
 * floating above the content -- and putting it here means the data starts
 * roughly a block higher up the page.
 */
export const FilterHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  flex: 1 1 auto;
`;

export const FilterHeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const FilterHeaderSearch = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 2 1 260px;
  min-width: 210px;

  /* The input takes the space left by its label, matching how the selects
     beside it fill their own fields. */
  .ant-input-group-wrapper {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

export const FilterHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

/** First row: the dimension filters, which narrow both tabs server-side. */
export const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;

  /* Both rows run their controls at the same height, so the bar reads as one
     bar rather than two strips of unrelated widgets.

     Search is a group (input + button) whose parts must be sized together:
     setting only the inner input leaves it floating inside a taller shell
     with the button standing proud of it. */
  .ant-select-selector,
  .ant-input-affix-wrapper,
  .ant-input,
  .ant-input-search-button {
    height: 32px !important;
  }

  .ant-input-affix-wrapper,
  .ant-input-search-button {
    display: inline-flex;
    align-items: center;
  }
`;

/** Label sits inline before its control. Stacked captions over three
 *  full-width selects cost a whole extra row of header for three words. */
export const FilterField = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 1 1 170px;
  min-width: 150px;
`;

export const FilterLabel = styled.label`
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: rgba(${APP_SHELL_THEME.overlayRgb}, 0.55);
  white-space: nowrap;
`;

/** Second row of the filter block: the view controls that apply to whichever
 *  tab is open. Separated from the dimension filters above by a hairline so
 *  the bar reads as two ideas -- which bookings, and how to show them. */
export const ViewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  padding-top: 9px;
  border-top: 1px solid rgba(${APP_SHELL_THEME.overlayRgb}, 0.08);

  /* antd's disabled fill is nearly the page colour on this dark ground, which
     left a disabled button reading as a smudge rather than a control that is
     currently unavailable. The text already clears contrast; this gives the
     button back its edge. */
  .ant-btn:disabled {
    border-color: rgba(${APP_SHELL_THEME.overlayRgb}, 0.18);
    background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.04);
  }
`;

export const ViewField = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
`;

export const ViewLabel = styled.span`
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: rgba(${APP_SHELL_THEME.overlayRgb}, 0.55);
  white-space: nowrap;
`;

export const ViewSpacer = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

/**
 * Timeline-only controls, recessed rather than removed on the table tab so the
 * bar does not change shape when tabs are switched.
 *
 * 0.7 rather than a heavier fade: at 0.45 the 10px labels fell to 4.42:1
 * against the page, under the 4.5:1 floor for text this size. Disabled state
 * already signals "not available here"; the opacity only needs to say
 * "secondary", not make the words hard to read.
 */
export const ViewGroup = styled.div<{ $inactive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  opacity: ${({ $inactive }) => ($inactive ? 0.7 : 1)};
  transition: opacity 140ms ease;
`;

/**
 * Table scroll wrapper — wide content (30+ possible columns) scrolls inside
 * this container instead of the page. The table itself sets `scroll.x`.
 */
export const TableScrollContainer = styled.div`
  min-width: 0;
  overflow-x: auto;

  .ant-table-wrapper {
    min-width: 100%;
  }

  /* A single-line campaign has nothing to expand, so antd renders an empty
     spacer where its neighbours show a "+". Reading down the column that gap
     looks like a row that failed to draw, so the spacer carries a dimmed
     marker instead: the left edge stays regular, and the difference between
     "nothing to open" and "not loaded" is visible. */
  .ant-table-row-expand-icon-spaced {
    position: relative;
    visibility: visible;
    background: transparent;
    border: 0;
    cursor: default;

    &::after {
      content: '';
      /* antd hides both pseudo-elements on the spacer; the marker needs its
         own display back before anything else here has an effect. */
      display: block;
      position: absolute;
      inset: 0;
      margin: auto;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${APP_SHELL_THEME.textMuted};
      opacity: 0.75;
    }
  }

  /* An open campaign and its line items read as one block: the campaign row
     is set in weight and the lines beneath it sit on a tinted band with an
     accent edge. Without this the nested rows were the same colour as the
     campaign rows around them, and read as more summaries rather than as the
     parts of the one above. */
  .ant-table-tbody > tr.ant-table-row:has(.ant-table-row-expand-icon-expanded) > td {
    font-weight: 600;
    border-bottom-color: transparent !important;
  }

  .ant-table-tbody > tr.ant-table-row:has(.ant-table-row-expand-icon-expanded) > td:first-child,
  .ant-table-tbody > tr.ant-table-expanded-row > td {
    box-shadow: inset 3px 0 0 ${APP_SHELL_THEME.accent};
  }

  .ant-table-tbody > tr.ant-table-expanded-row > td {
    background: ${APP_SHELL_THEME.lineItemBg} !important;
    padding: 2px 0 10px 12px !important;
  }

  /* antd pulls a nested table out by -16px on each side to cancel the cell's
     default 16px padding. With the padding tightened above, that margin drew
     the line items up over the campaign row and clipped its text. */
  .ant-table-tbody > tr.ant-table-expanded-row > td .ant-table-wrapper .ant-table {
    margin: 0 0 0 32px !important;
  }

  .ant-table-expanded-row .ant-table-tbody > tr > td,
  .ant-table-expanded-row .ant-table-tbody > tr > td.ant-table-cell-fix-right {
    background: ${APP_SHELL_THEME.lineItemBg} !important;
    border-bottom-color: rgba(97, 97, 215, 0.16) !important;
  }

  .ant-table-expanded-row .ant-table-tbody > tr:hover > td,
  .ant-table-expanded-row .ant-table-tbody > tr:hover > td.ant-table-cell-fix-right {
    background: ${APP_SHELL_THEME.lineItemHoverBg} !important;
  }

  /* The nested header repeats the page's columns, so it is kept for
     orientation but demoted to a caption: no fill, muted, smaller. As a
     second full header it competed with the real one and the campaign row. */
  .ant-table-expanded-row .ant-table-thead > tr > th,
  .ant-table-expanded-row .ant-table-thead > tr > th.ant-table-cell-fix-right {
    background: ${APP_SHELL_THEME.lineItemBg} !important;
    color: ${APP_SHELL_THEME.textMuted} !important;
    font-size: 11px;
    font-weight: 500;
    padding-block: 5px !important;
    border-bottom: 1px solid rgba(97, 97, 215, 0.3) !important;

    &::before {
      display: none;
    }
  }
`;

export const ColumnTogglePanel = styled.div`
  max-height: 360px;
  overflow-y: auto;
  padding: 4px 4px 0;

  .ant-checkbox-wrapper {
    display: flex;
    padding: 4px 0;
  }
`;

/**
 * A quiet note above the summary figures.
 *
 * Deliberately not an Alert: this reports a few line items against a page
 * totalling tens of millions, so it needs to be noticed without taking the
 * top of the screen. Muted until hovered, which is enough for something
 * worth knowing rather than acting on immediately.
 */
export const AwaitingNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid ${APP_SHELL_THEME.metricCardBorder};
  background: ${APP_SHELL_THEME.metricCardBg};
  color: ${APP_SHELL_THEME.textMuted};
  font-size: 12px;
  cursor: default;

  .anticon {
    font-size: 13px;
    flex-shrink: 0;
  }

  &:hover {
    color: ${APP_SHELL_THEME.text};
  }
`;

/** A campaign column whose lines disagree. Muted and italic so it reads as a
 *  description of the lines rather than as a value someone entered. */
export const MixedValue = styled.span`
  color: ${APP_SHELL_THEME.textMuted};
  font-style: italic;
`;
